import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../../../store/store';
import { useParams } from 'react-router-dom';
import { useAbilityContext } from '../../../../../components/Can/Can';
import { Action } from '../../../../../enums/action';
import { PAGE_TITLE } from '../../../../../constants/common';
import { ActiveCycleChannelConflict, Cycle } from '../../../../../store/models/cycles';
import CycleStatus from '../../../../../enums/cycle-status.enum';

export const useDeletedCycleContract = () => {
  const [cycleToRestore, setCycleToRestore] = useState<Cycle | null>(null);
  const [restoreTargetStatus, setRestoreTargetStatus] = useState<CycleStatus | null>(null);
  const [conflicts, setConflicts] = useState<ActiveCycleChannelConflict[]>([]);
  const [isLoadingConflicts, setIsLoadingConflicts] = useState(false);
  const { clientId } = useParams();

  const {
    cycles: {
      restoreCycle,
      getCycleChannelConflicts,
      getDeletedCyclesByLocation,
      getCyclesCountByLocation,
    },
  } = useAppDispatch();

  const ability = useAbilityContext();

  // Only whoever can delete a cycle (Owner/Supervisor today) can restore one.
  const canRestoreCycle = useMemo(() => {
    return ability.can(Action.Delete, PAGE_TITLE.EDIT_CYCLE);
  }, [ability]);

  const dataRefetch = async (locationId: string) => {
    await Promise.all([
      getCyclesCountByLocation({ locationId }),
      getDeletedCyclesByLocation(locationId),
    ]);
  };

  const openSelectedCycleRestore = useCallback(async (cycle: Cycle) => {
    setCycleToRestore(cycle);
    setRestoreTargetStatus(cycle.status ?? CycleStatus.InActive);
    setIsLoadingConflicts(true);

    try {
      const result = await getCycleChannelConflicts(cycle.id);
      setConflicts(result);
    } finally {
      setIsLoadingConflicts(false);
    }
  }, []);

  const closeSelectedCycleRestore = useCallback(() => {
    setCycleToRestore(null);
    setRestoreTargetStatus(null);
    setConflicts([]);
  }, []);

  const onAcceptSelectedCycleRestore = useCallback(async () => {
    if (!cycleToRestore) {
      return;
    }

    const id = cycleToRestore.id;
    const status = restoreTargetStatus;
    closeSelectedCycleRestore();

    await restoreCycle({ id, status });

    await dataRefetch(clientId);
  }, [cycleToRestore, restoreTargetStatus, closeSelectedCycleRestore, clientId]);

  return {
    canRestoreCycle,

    cycleToRestore,
    restoreTargetStatus,
    setRestoreTargetStatus,
    conflicts,
    isLoadingConflicts,
    openSelectedCycleRestore,
    closeSelectedCycleRestore,
    onAcceptSelectedCycleRestore,
  };
};
